import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Copy, CheckCircle, Loader2, XCircle, Clock, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import QuickNav from '@/components/QuickNav';

import { BACKEND_URL } from '@/lib/api';

const PixPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, pending, success, error
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes
  
  const packageId = searchParams.get('package') || 'basic_monthly';
  const transactionId = searchParams.get('transaction_id');

  useEffect(() => {
    if (transactionId) {
      // Check existing transaction status
      checkPixStatus();
    } else {
      // Create new PIX payment
      createPixPayment();
    }
  }, [transactionId]);

  // Countdown timer
  useEffect(() => {
    if (status === 'pending' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [status, timeLeft]);

  // Poll for payment status
  useEffect(() => {
    if (status === 'pending' && pixData?.transaction_id) {
      const pollInterval = setInterval(() => {
        checkPixStatus();
      }, 5000); // Check every 5 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [status, pixData]);

  const createPixPayment = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      // Use BACKEND_URL as origin (it's the production URL)
      const originUrl = BACKEND_URL.replace('/api', '').replace('http://', 'https://');
      
      const response = await fetch(`${BACKEND_URL}/api/payments/pix/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        },
        body: JSON.stringify({
          package_id: packageId,
          payer_email: user?.email || 'cliente@email.com',
          payer_name: user?.name || 'Cliente',
          origin_url: originUrl
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPixData(data);
        setStatus('pending');
        
        // Update URL with transaction_id
        window.history.replaceState(null, '', `/pix?package=${packageId}&transaction_id=${data.transaction_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar pagamento PIX');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error creating PIX:', error);
      toast.error('Erro ao criar pagamento PIX');
      setStatus('error');
    }
  };

  const checkPixStatus = async () => {
    const txnId = transactionId || pixData?.transaction_id;
    if (!txnId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/pix/status/${txnId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'approved' || data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Pagamento confirmado!');
        } else if (!pixData && data.qr_code) {
          setPixData(data);
          setStatus('pending');
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const copyPixCode = async () => {
    if (pixData?.qr_code) {
      try {
        await navigator.clipboard.writeText(pixData.qr_code);
        setCopied(true);
        toast.success('Código PIX copiado!');
        setTimeout(() => setCopied(false), 3000);
      } catch (error) {
        toast.error('Erro ao copiar código');
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPackageInfo = () => {
    if (packageId === 'premium_monthly') {
      return { name: 'Plano Premium', price: 'R$ 29,90' };
    }
    return { name: 'Plano Básico', price: 'R$ 9,90' };
  };

  const packageInfo = getPackageInfo();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        className="max-w-md w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Loading */}
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-[#00FF94] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-4">
              Gerando PIX...
            </h1>
            <p className="text-gray-400">
              Aguarde enquanto preparamos seu pagamento
            </p>
          </div>
        )}

        {/* Pending - Show QR Code */}
        {status === 'pending' && pixData && (
          <div className="text-center">
            {/* Header */}
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-[#00FF94]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Pague com PIX
              </h1>
              <p className="text-gray-400">
                Escaneie o QR Code ou copie o código
              </p>
            </div>

            {/* Package Info */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{packageInfo.name}</span>
                <span className="text-2xl font-bold text-[#00FF94]">{packageInfo.price}</span>
              </div>
            </div>

            {/* QR Code */}
            {pixData.qr_code_base64 && (
              <div className="bg-white rounded-2xl p-4 mb-6 inline-block">
                <img 
                  src={`data:image/png;base64,${pixData.qr_code_base64}`}
                  alt="QR Code PIX"
                  className="w-48 h-48 mx-auto"
                />
              </div>
            )}

            {/* Copy Code Button */}
            <div className="mb-6">
              <Button
                onClick={copyPixCode}
                className={`w-full py-6 rounded-xl font-semibold ${
                  copied 
                    ? 'bg-green-500 text-white' 
                    : 'bg-[#00FF94] text-black hover:bg-[#00CC76]'
                }`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Código Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-2" />
                    Copiar Código PIX
                  </>
                )}
              </Button>
            </div>

            {/* Timer */}
            <div className="flex items-center justify-center gap-2 text-gray-400 mb-6">
              <Clock className="w-4 h-4" />
              <span>Expira em {formatTime(timeLeft)}</span>
            </div>

            {/* Status indicator */}
            <div className="bg-[#0A0A0A] border border-yellow-500/20 rounded-xl p-4">
              <div className="flex items-center justify-center gap-2 text-yellow-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Aguardando pagamento...</span>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                O status será atualizado automaticamente
              </p>
            </div>

            {/* Cancel */}
            <Button
              variant="ghost"
              className="mt-6 text-gray-400 hover:text-white"
              onClick={() => navigate('/pricing')}
            >
              Cancelar e voltar
            </Button>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#00FF94]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Pagamento Confirmado!
            </h1>
            <p className="text-gray-400 mb-8">
              Seu {packageInfo.name} foi ativado com sucesso!
            </p>
            
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-8">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Valor pago</span>
                <span className="text-2xl font-bold text-[#00FF94]">{packageInfo.price}</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                onClick={() => navigate('/dashboard')}
              >
                Ir para o Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5 rounded-full"
                onClick={() => navigate('/')}
              >
                Voltar ao Início
              </Button>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Erro no Pagamento
            </h1>
            <p className="text-gray-400 mb-8">
              Não foi possível gerar o PIX. Por favor, tente novamente.
            </p>
            
            <div className="space-y-3">
              <Button
                className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                onClick={() => {
                  setStatus('loading');
                  createPixPayment();
                }}
              >
                Tentar Novamente
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5 rounded-full"
                onClick={() => navigate('/pricing')}
              >
                Voltar aos Planos
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 flex items-center justify-center gap-2 text-gray-500">
          <Shield className="w-4 h-4" />
          <span className="text-sm">Pagamento seguro via Mercado Pago</span>
        </div>
      </motion.div>
      <QuickNav />
    </div>
  );
};

export default PixPayment;
